/**
 * FE#156 — decide the CTA a plan tile shows relative to the user's active
 * subscription (Upgrade / Current / Update / Downgrade), WITHOUT ever inferring a
 * downgrade from a missing field.
 *
 * The previous inline scoring (plan-selection.tsx) folded (order, seatCount,
 * interval) into one additive number:
 *
 *     score = order*1000 + seatCount*100 + intervalMonths   // month=1, else=12
 *
 * For a no-subscription (Free) user every `activeSubscription?.x ?? default`
 * fallback fired, giving a PHANTOM baseline of `0*1000 + 1*100 + 12 = 112` — one
 * seat and an annual interval baked in. A target plan whose optional `order` was
 * absent then scored 101 (monthly) or 112 (annual), i.e. below/equal to 112, so
 * Pro rendered "Downgrade" (no price) or "Current Plan" to a Free user and the
 * sale was blocked. See serenix-com/whatsapp-scheduler#156.
 *
 * Fixes encoded here:
 *  - The no-subscription case is decided by BRANCH, not by an additive score, so
 *    no phantom seat/interval baseline can ever outrank a real plan.
 *  - `order` is compared FIRST; seats then interval only break ties WITHIN the
 *    same order (they can never outweigh an order difference).
 *  - A missing `order` on a paid target tile yields Upgrade, never Downgrade — a
 *    wrongly-offered upgrade is recoverable; a wrongly-blocked one is a lost sale.
 *
 * Kept as a pure function so the decision is unit-testable without rendering the
 * (large) plan-selection component. Mirrors the sibling `cancel-cta.ts` pattern.
 */

export type PlanCta = 'upgrade' | 'current' | 'update' | 'downgrade';

export interface PlanCtaTarget {
  /** Plan tier rank from the catalogue. OPTIONAL — may be absent. */
  order?: number;
  seatCount?: number;
  /** 'month' | 'year' | ... — anything other than 'month' is treated as annual. */
  intervalUnit?: string;
  /** True when this tile is the Free plan card (paymentPlanData.key === 'Free'). */
  isFreePlan: boolean;
}

export interface ActiveSubscriptionLike {
  order?: number;
  seatCount?: number;
  /** 'month' | 'year' */
  billingPeriod?: string;
  /** 'active' | 'non_renewing' | 'cancelled' | ... */
  status?: string;
}

export interface PlanCtaDecision {
  cta: PlanCta;
  isCurrentPlan: boolean;
  isUpgrade: boolean;
}

/** month => 1, anything else (incl. undefined) => 12. Tie-break weight only. */
const intervalMonths = (unit?: string): number => (unit === 'month' ? 1 : 12);

/** Lexicographic compare of equal-length numeric tuples. */
const compareTuple = (a: number[], b: number[]): number => {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
};

export const resolvePlanCta = (
  target: PlanCtaTarget,
  active: ActiveSubscriptionLike | undefined | null
): PlanCtaDecision => {
  const hasPaidSubscription = (active?.order ?? 0) > 0;
  // #766 - a no-card `in_trial` Pro is entitled as Pro (order 3) but has NOT paid. It must
  // never see its trial tier as "Current Plan" (no pay CTA) or lower tiers as "Downgrade":
  // every paid tile is an Upgrade (checkout) until a real paid subscription exists.
  const isInTrial = active?.status === 'in_trial';

  // The Free tile is always the lowest tier — decide it directly so a missing
  // Free `order` can never be mistaken for an upgrade.
  if (target.isFreePlan) {
    return hasPaidSubscription
      ? { cta: 'downgrade', isCurrentPlan: false, isUpgrade: false }
      : { cta: 'current', isCurrentPlan: true, isUpgrade: false };
  }

  // A user with no paid subscription is on Free → every paid tile is an Upgrade,
  // even when the target plan's optional `order` is absent. (No additive score,
  // so the phantom-112 baseline that produced the bug cannot exist.)
  if (!hasPaidSubscription || isInTrial) {
    return { cta: 'upgrade', isCurrentPlan: false, isUpgrade: true };
  }

  // Paid user viewing a paid tile. Never infer a downgrade from a missing order.
  if (target.order == null) {
    return { cta: 'upgrade', isCurrentPlan: false, isUpgrade: true };
  }

  // order dominates; seats then interval break ties only within the same order.
  const cmp = compareTuple(
    [target.order, target.seatCount ?? 1, intervalMonths(target.intervalUnit)],
    [active!.order ?? 0, active!.seatCount ?? 1, intervalMonths(active!.billingPeriod)]
  );

  if (cmp > 0) return { cta: 'upgrade', isCurrentPlan: false, isUpgrade: true };
  if (cmp < 0) return { cta: 'downgrade', isCurrentPlan: false, isUpgrade: false };

  // Same tier = the user's current plan. Mirror the prior UI: 'update' when the
  // subscription is not active (e.g. non_renewing), 'current' otherwise.
  const cta: PlanCta = active!.status === 'active' ? 'current' : 'update';
  return { cta, isCurrentPlan: true, isUpgrade: false };
};
